<template>
  <div>
    <v-layout align-center justify-space-between class="mb-3">
      <span class="title grey--text text--darken-2">Provider Requests</span>
      <v-btn icon small :loading="loading" @click="load">
        <v-icon>refresh</v-icon>
      </v-btn>
    </v-layout>

    <v-tabs
      v-model="activeTab"
      color="primary"
      slider-color="white"
      class="mb-4"
    >
      <v-tab class="white--text">Pending</v-tab>
      <v-tab class="white--text">Approved</v-tab>
      <v-tab class="white--text">Declined</v-tab>
    </v-tabs>

    <div v-if="loading" class="text-xs-center pa-4">
      <v-progress-circular indeterminate color="secondary" />
    </div>

    <div v-else-if="!filtered.length" class="caption grey--text pa-2">
      No {{ tabLabel }} provider requests.
    </div>

    <v-card
      v-for="provider in filtered"
      :key="provider.id"
      class="mb-3 pa-3"
      style="cursor: pointer"
      @click="openProvider(provider)"
    >
      <v-layout wrap align-start>
        <v-flex xs12 sm8>
          <div class="subheading font-weight-medium">
            {{ provider.first_name }} {{ provider.last_name }}
          </div>
          <div class="body-2 grey--text">{{ provider.email }}</div>
          <div class="body-2 mt-1">
            <strong>Organization:</strong> {{ provider.organization }}
          </div>
          <div v-if="provider.phone" class="body-2">
            <strong>Phone:</strong> {{ provider.phone }}
          </div>
          <div
            v-if="
              provider.requested_services && provider.requested_services.length
            "
            class="body-2"
          >
            <strong>Services requested:</strong>
            {{ provider.requested_services.join(", ") }}
          </div>
          <div class="body-2 mt-1">
            <strong>Reason:</strong> {{ provider.reason }}
          </div>
          <div class="caption grey--text mt-1">
            Submitted {{ formatDate(provider.created_at) }}
          </div>
        </v-flex>

        <v-flex xs12 sm4 class="text-sm-right mt-2 mt-sm-0">
          <v-chip
            small
            :color="statusColor(provider.status)"
            :text-color="statusTextColor(provider.status)"
            class="mb-2"
          >
            {{ provider.status }}
          </v-chip>

          <div v-if="provider.status === 'pending'" class="mt-1">
            <v-btn
              small
              depressed
              color="success"
              class="white--text mr-1"
              :loading="actionLoading === provider.id + '-approve'"
              @click.stop="approve(provider)"
            >
              Approve
            </v-btn>
            <v-btn
              small
              depressed
              color="error"
              class="white--text"
              :loading="actionLoading === provider.id + '-decline'"
              @click.stop="decline(provider)"
            >
              Decline
            </v-btn>
          </div>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Provider detail modal -->
    <v-dialog v-model="detailDialog" max-width="700" scrollable>
      <v-card v-if="selectedProvider">
        <v-card-title class="primary white--text py-3">
          <span class="title">
            {{ selectedProvider.first_name }} {{ selectedProvider.last_name }}
          </span>
          <v-spacer />
          <v-btn icon dark @click="detailDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" style="max-height: 70vh">
          <div class="body-2 grey--text mb-1">{{ selectedProvider.email }}</div>
          <div class="body-2 mb-1">
            <strong>Organization:</strong> {{ selectedProvider.organization }}
          </div>
          <div v-if="selectedProvider.phone" class="body-2 mb-1">
            <strong>Phone:</strong> {{ selectedProvider.phone }}
          </div>

          <v-divider class="my-3" />

          <v-layout align-center justify-space-between class="mb-3">
            <span class="subheading font-weight-medium">Listings</span>
            <v-btn
              small
              depressed
              color="primary"
              @click="openAssignListingDialog"
            >
              <v-icon left small>person_add</v-icon>
              Assign Listing
            </v-btn>
          </v-layout>

          <div v-if="listingsLoading" class="text-xs-center pa-4">
            <v-progress-circular indeterminate color="secondary" />
          </div>

          <div v-else-if="!providerListings.length" class="caption grey--text">
            This provider has no listings.
          </div>

          <v-card
            v-for="listing in providerListings"
            :key="listing.id"
            outlined
            class="mb-3 pa-3"
          >
            <v-layout align-start justify-space-between>
              <div style="flex: 1; min-width: 0">
                <div class="body-2 font-weight-medium">{{ listing.title }}</div>
                <div
                  class="caption grey--text mt-1"
                  style="white-space: pre-wrap; word-break: break-word"
                >
                  {{ listing.description | truncate(120) }}
                </div>
                <div v-if="listing.address" class="caption mt-1">
                  <v-icon x-small>place</v-icon> {{ listing.address }}
                </div>
                <div v-if="listing.website" class="caption mt-1">
                  <v-icon x-small>link</v-icon> {{ listing.website }}
                </div>
              </div>
              <v-chip
                x-small
                :color="listingStatusColor(listing.status)"
                :text-color="listingStatusTextColor(listing.status)"
                class="ml-2 mt-1"
                style="flex-shrink: 0"
              >
                {{ listing.status }}
              </v-chip>
            </v-layout>
          </v-card>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3">
          <v-btn
            depressed
            color="error"
            class="white--text"
            @click="confirmDelete"
          >
            <v-icon left small>delete</v-icon>
            Delete Provider
          </v-btn>
          <v-spacer />
          <v-btn flat @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation dialog -->
    <v-dialog v-model="deleteDialog" max-width="580" persistent>
      <v-card>
        <v-card-title class="headline">Delete Provider?</v-card-title>
        <v-card-text>
          Are you sure you want to delete
          <strong v-if="selectedProvider">
            {{ selectedProvider.first_name }}
            {{ selectedProvider.last_name }} </strong
          >?
          <span v-if="providerListings.length">
            They have {{ providerListings.length }}
            {{ providerListings.length === 1 ? "listing" : "listings" }}.
            Deleted listings will still appear in the link click statistics
            table.
          </span>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-btn flat :disabled="deleteLoading" @click="deleteDialog = false">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="providerListings.length"
            depressed
            color="error"
            class="white--text"
            :loading="deleteLoading === 'with'"
            @click="doDelete(true)"
          >
            Delete provider &amp; listings
          </v-btn>
          <v-btn
            depressed
            color="error"
            class="white--text"
            :loading="deleteLoading === 'without'"
            @click="doDelete(false)"
          >
            Delete provider only
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign listing dialog -->
    <v-dialog v-model="assignListingDialog" max-width="520">
      <v-card>
        <v-card-title class="headline">Assign Listing</v-card-title>
        <div v-if="selectedProvider" class="pb-0 px-3">
          To {{ selectedProvider.first_name }} {{ selectedProvider.last_name }}
        </div>
        <v-card-text class="pt-3">
          <v-autocomplete
            v-model="assignListingId"
            :items="assignListingItems"
            item-text="label"
            item-value="value"
            label="Listing"
            placeholder="Search listings…"
            clearable
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title>{{ item.title }}</v-list-tile-title>
                <v-list-tile-sub-title class="caption">
                  {{ item.ownerLabel }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-btn
            flat
            :disabled="assignListingLoading"
            @click="assignListingDialog = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            depressed
            color="primary"
            :disabled="!assignListingId"
            :loading="assignListingLoading"
            @click="submitAssignListing"
          >
            Assign
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
      {{ snackbar.message }}
      <v-btn flat @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "AdminProviders",
  filters: {
    truncate(value, length) {
      if (!value) return "";
      return value.length > length ? value.slice(0, length) + "…" : value;
    }
  },
  data() {
    return {
      activeTab: 0,
      loading: false,
      actionLoading: null,
      providers: [],
      selectedProvider: null,
      providerListings: [],
      listingsLoading: false,
      detailDialog: false,
      deleteDialog: false,
      deleteLoading: false,
      assignListingDialog: false,
      assignListingId: null,
      assignListingLoading: false,
      snackbar: { show: false, message: "", color: "success" }
    };
  },
  computed: {
    tabLabel() {
      return ["pending", "approved", "declined"][this.activeTab];
    },
    filtered() {
      return this.providers.filter(p => p.status === this.tabLabel);
    },
    providerMap() {
      const map = {};
      this.providers.forEach(p => {
        map[p.id] = `${p.first_name} ${p.last_name}`;
      });
      return map;
    },
    assignListingItems() {
      const selectedId = this.selectedProvider && this.selectedProvider.id;
      return this.$store.state.listings
        .filter(
          l => !selectedId || !(l.provider_ids || []).includes(selectedId)
        )
        .map(l => ({
          value: l.id,
          label: l.title,
          title: l.title,
          ownerLabel:
            l.provider_ids && l.provider_ids.length
              ? l.provider_ids
                  .map(id => this.providerMap[id] || "Unknown")
                  .join(", ")
              : "Unassigned"
        }));
    }
  },
  mounted() {
    this.load();
    this.$store.dispatch("fetchListings");
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.providers = await this.$store.dispatch("fetchProviders");
      } catch {
        this.notify("Failed to load provider requests.", "error");
      } finally {
        this.loading = false;
      }
    },

    async openProvider(provider) {
      this.selectedProvider = provider;
      this.providerListings = [];
      this.detailDialog = true;
      this.listingsLoading = true;
      const requestedId = provider.id;
      const isStillCurrent = () =>
        this.selectedProvider && this.selectedProvider.id === requestedId;
      try {
        const listings = await this.$store.dispatch(
          "fetchProviderListingsAdmin",
          provider.id
        );
        if (isStillCurrent()) {
          this.providerListings = listings;
        }
      } catch {
        if (isStillCurrent()) {
          this.notify("Failed to load listings.", "error");
        }
      } finally {
        if (isStillCurrent()) {
          this.listingsLoading = false;
        }
      }
    },

    confirmDelete() {
      this.deleteDialog = true;
    },

    async doDelete(deleteListings) {
      this.deleteLoading = deleteListings ? "with" : "without";
      try {
        await this.$store.dispatch("deleteProvider", {
          id: this.selectedProvider.id,
          deleteListings
        });
        const name = `${this.selectedProvider.first_name} ${this.selectedProvider.last_name}`;
        this.providers = this.providers.filter(
          p => p.id !== this.selectedProvider.id
        );
        this.deleteDialog = false;
        this.detailDialog = false;
        this.selectedProvider = null;
        this.notify(`${name} deleted.`);
      } catch {
        this.notify("Failed to delete provider.", "error");
      } finally {
        this.deleteLoading = false;
      }
    },

    async approve(provider) {
      this.actionLoading = `${provider.id}-approve`;
      try {
        await this.$store.dispatch("approveProvider", provider.id);
        provider.status = "approved";
        this.notify(`${provider.first_name} ${provider.last_name} approved.`);
      } catch {
        this.notify("Failed to approve provider.", "error");
      } finally {
        this.actionLoading = null;
      }
    },

    async decline(provider) {
      this.actionLoading = `${provider.id}-decline`;
      try {
        await this.$store.dispatch("declineProvider", provider.id);
        provider.status = "declined";
        this.notify(`${provider.first_name} ${provider.last_name} declined.`);
      } catch {
        this.notify("Failed to decline provider.", "error");
      } finally {
        this.actionLoading = null;
      }
    },

    formatDate(date) {
      return moment(date).format("MMM D, YYYY h:mm A");
    },

    statusColor(status) {
      return (
        {
          pending: "orange lighten-4",
          approved: "green lighten-4",
          declined: "red lighten-4"
        }[status] || "grey lighten-3"
      );
    },

    statusTextColor(status) {
      return (
        {
          pending: "orange darken-2",
          approved: "green darken-2",
          declined: "red darken-2"
        }[status] || "grey darken-2"
      );
    },

    listingStatusColor(status) {
      return (
        {
          approved: "green lighten-4",
          pending_new: "blue lighten-4",
          pending_edit: "blue lighten-4",
          declined: "red lighten-4"
        }[status] || "grey lighten-3"
      );
    },

    listingStatusTextColor(status) {
      return (
        {
          approved: "green darken-2",
          pending_new: "blue darken-2",
          pending_edit: "blue darken-2",
          declined: "red darken-2"
        }[status] || "grey darken-2"
      );
    },

    openAssignListingDialog() {
      this.assignListingId = null;
      this.assignListingDialog = true;
    },

    async submitAssignListing() {
      if (!this.assignListingId) return;
      this.assignListingLoading = true;
      try {
        const listing = this.$store.state.listings.find(
          l => l.id === this.assignListingId
        );
        const currentIds = (listing && listing.provider_ids) || [];
        const mergedIds = [
          ...new Set([...currentIds, this.selectedProvider.id])
        ];
        await this.$store.dispatch("assignListing", {
          listingId: this.assignListingId,
          providerIds: mergedIds
        });
        this.assignListingDialog = false;
        this.providerListings = await this.$store.dispatch(
          "fetchProviderListingsAdmin",
          this.selectedProvider.id
        );
        this.notify("Listing assigned successfully.");
      } catch {
        this.notify("Failed to assign listing.", "error");
      } finally {
        this.assignListingLoading = false;
      }
    },

    notify(message, color = "success") {
      this.snackbar = { show: true, message, color };
    }
  }
};
</script>
